import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Heart, Sun, Activity, Clock, Users, Coffee, Wind, AlertTriangle, TrendingUp } from 'lucide-react';

interface ZoneAirQuality {
  zone: string;
  footTraffic: number;
  particulates: number;
  risk: 'Low' | 'Moderate' | 'High';
  lastCleaned: string;
  recommendedFlooring: {
    product: string;
    features: string[];
  };
}

const WellnessMetrics: React.FC = () => {
  // Mock data - in real app this would come from various sensors
  const wellnessData = {
    activityMetrics: {
      standingTime: {
        average: '2.5',
        trend: '+15%',
        recommendation: 'On target'
      },
      sittingTime: {
        average: '4.8',
        trend: '-10%',
        recommendation: 'Consider more breaks'
      },
      movement: {
        average: '45',
        trend: '+5%',
        recommendation: 'Good activity level'
      }
    },
    ergonomics: {
      standingDesks: {
        total: 85,
        inUse: 45,
        utilizationRate: 53
      },
      breakReminders: {
        sent: 156,
        acknowledged: 123,
        compliance: 79
      }
    },
    environmentalComfort: {
      naturalLight: {
        score: 85,
        trend: 'increasing',
        areas: [
          { zone: 'North Wing', score: 92 },
          { zone: 'South Wing', score: 88 },
          { zone: 'East Wing', score: 82 },
          { zone: 'West Wing', score: 78 }
        ]
      },
      noiseLevel: {
        current: 48,
        peak: 65,
        quiet: 35,
        zones: [
          { name: 'Open Office', level: 52, status: 'Moderate' },
          { name: 'Quiet Zones', level: 38, status: 'Optimal' },
          { name: 'Meeting Areas', level: 58, status: 'Moderate' },
          { name: 'Break Room', level: 62, status: 'High' }
        ]
      }
    },
    breakPatterns: {
      hourlyData: [
        { time: '9AM', count: 15, avgDuration: 12 },
        { time: '10AM', count: 22, avgDuration: 10 },
        { time: '11AM', count: 18, avgDuration: 15 },
        { time: '12PM', count: 45, avgDuration: 25 },
        { time: '1PM', count: 38, avgDuration: 22 },
        { time: '2PM', count: 25, avgDuration: 12 },
        { time: '3PM', count: 30, avgDuration: 15 },
        { time: '4PM', count: 20, avgDuration: 10 },
        { time: '5PM', count: 12, avgDuration: 8 }
      ],
      popularAreas: [
        { area: 'Coffee Bar', usage: 85, peakTime: '10:30 AM' },
        { area: 'Outdoor Terrace', usage: 65, peakTime: '12:30 PM' },
        { area: 'Relaxation Room', usage: 45, peakTime: '2:30 PM' },
        { area: 'Game Area', usage: 35, peakTime: '3:30 PM' }
      ]
    },
    airQualityData: {
      zones: [
        {
          zone: 'Main Corridor',
          footTraffic: 2450,
          particulates: 85,
          risk: 'High' as const,
          lastCleaned: '2 hours ago',
          recommendedFlooring: {
            product: 'Commercial Grade Vinyl Sheet Flooring',
            features: ['Superior durability', 'Infection control', 'Slip resistant', 'Low maintenance']
          }
        },
        {
          zone: 'Open Office North',
          footTraffic: 1850,
          particulates: 65,
          risk: 'Moderate' as const,
          lastCleaned: '4 hours ago',
          recommendedFlooring: {
            product: 'Premium Carpet Tiles',
            features: ['Acoustic performance', 'Ergonomic comfort', 'Stain resistant', 'Modular design']
          }
        },
        {
          zone: 'Meeting Areas',
          footTraffic: 1200,
          particulates: 45,
          risk: 'Low' as const,
          lastCleaned: '6 hours ago',
          recommendedFlooring: {
            product: 'Luxury Vinyl Tiles',
            features: ['No-polish option', 'Superior acoustics', 'Easy installation', 'Easy maintenance']
          }
        },
        {
          zone: 'Break Room',
          footTraffic: 950,
          particulates: 55,
          risk: 'Moderate' as const,
          lastCleaned: '3 hours ago',
          recommendedFlooring: {
            product: 'Safety Sheet Vinyl',
            features: ['Enhanced slip resistance', 'Stain protection', 'Impact resistant', 'Moisture resistant']
          }
        }
      ],
      buildingAverage: {
        particulates: 62,
        trend: '-5%',
        lastUpdate: '2 minutes ago'
      }
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'high':
        return 'text-red-500';
      case 'moderate':
        return 'text-yellow-500';
      case 'optimal':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  const getTrendColor = (trend: string) => {
    if (trend.startsWith('+')) return 'text-green-500';
    if (trend.startsWith('-')) return 'text-red-500';
    return 'text-gray-500';
  };

  const getRiskColor = (risk: string) => {
    switch (risk.toLowerCase()) {
      case 'high':
        return 'text-red-500';
      case 'moderate':
        return 'text-yellow-500';
      case 'low':
        return 'text-green-500';
      default:
        return 'text-gray-500';
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Wellness & Comfort Metrics</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Movement</h3>
          </div>
          <p className="text-3xl font-bold">{wellnessData.activityMetrics.movement.average}</p>
          <p className="text-gray-500">mins/day active</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Sun className="text-yellow-500 mr-2" />
            <h3 className="text-lg font-semibold">Natural Light</h3>
          </div>
          <p className="text-3xl font-bold">{wellnessData.environmentalComfort.naturalLight.score}</p>
          <p className="text-gray-500">Comfort score</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Heart className="text-red-500 mr-2" />
            <h3 className="text-lg font-semibold">Break Time</h3>
          </div>
          <p className="text-3xl font-bold">{wellnessData.ergonomics.breakReminders.compliance}%</p>
          <p className="text-gray-500">Break compliance</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Standing Desks</h3>
          </div>
          <p className="text-3xl font-bold">{wellnessData.ergonomics.standingDesks.utilizationRate}%</p>
          <p className="text-gray-500">Current usage</p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Activity Patterns</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Standing Time</p>
                <p className="text-sm text-gray-500">{wellnessData.activityMetrics.standingTime.average}h daily average</p>
              </div>
              <span className={`text-sm font-medium ${getTrendColor(wellnessData.activityMetrics.standingTime.trend)}`}>
                {wellnessData.activityMetrics.standingTime.trend}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Sitting Time</p>
                <p className="text-sm text-gray-500">{wellnessData.activityMetrics.sittingTime.average}h daily average</p>
              </div>
              <span className={`text-sm font-medium ${getTrendColor(wellnessData.activityMetrics.sittingTime.trend)}`}>
                {wellnessData.activityMetrics.sittingTime.trend}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium">Movement</p>
                <p className="text-sm text-gray-500">{wellnessData.activityMetrics.movement.average} minutes active</p>
              </div>
              <span className={`text-sm font-medium ${getTrendColor(wellnessData.activityMetrics.movement.trend)}`}>
                {wellnessData.activityMetrics.movement.trend}
              </span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Noise Levels</h3>
          <div className="space-y-4">
            {wellnessData.environmentalComfort.noiseLevel.zones.map((zone, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{zone.name}</p>
                  <p className={`text-sm ${getStatusColor(zone.status)}`}>{zone.status}</p>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className={`rounded-full h-2 ${
                        zone.level > 60 ? 'bg-red-500' :
                        zone.level > 50 ? 'bg-yellow-500' : 'bg-green-500'
                      }`}
                      style={{ width: `${(zone.level / 80) * 100}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{zone.level} dB</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Break Patterns</h3>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={wellnessData.breakPatterns.hourlyData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="time" />
              <YAxis yAxisId="left" orientation="left" stroke="#8884d8" />
              <YAxis yAxisId="right" orientation="right" stroke="#82ca9d" />
              <Tooltip />
              <Legend />
              <Bar 
                yAxisId="left"
                dataKey="count" 
                fill="#8884d8" 
                name="Number of Breaks"
              />
              <Bar 
                yAxisId="right"
                dataKey="avgDuration" 
                fill="#82ca9d" 
                name="Avg Duration (min)"
              />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Popular Break Areas</h3>
          <div className="space-y-4">
            {wellnessData.breakPatterns.popularAreas.map((area, index) => (
              <div key={index} className="flex items-center justify-between">
                <div>
                  <p className="font-medium">{area.area}</p>
                  <p className="text-sm text-gray-500">Peak: {area.peakTime}</p>
                </div>
                <div className="flex items-center">
                  <div className="w-32 bg-gray-200 rounded-full h-2 mr-3">
                    <div
                      className="bg-purple-500 rounded-full h-2"
                      style={{ width: `${area.usage}%` }}
                    ></div>
                  </div>
                  <span className="font-medium">{area.usage}%</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-xl font-semibold">Air Quality & Flooring Analysis</h3>
            <div className="text-sm text-gray-500">
              Last updated: {wellnessData.airQualityData.buildingAverage.lastUpdate}
            </div>
          </div>

          <div className="space-y-6">
            {wellnessData.airQualityData.zones.map((zone, index) => (
              <div key={index} className="border-b last:border-b-0 pb-6 last:pb-0">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="font-semibold text-lg">{zone.zone}</h4>
                    <div className="flex items-center mt-1">
                      <Users className="text-gray-400 h-4 w-4 mr-1" />
                      <span className="text-sm text-gray-600">{zone.footTraffic} paths/day</span>
                      <span className="mx-2">•</span>
                      <span className={`text-sm font-medium ${getRiskColor(zone.risk)}`}>
                        {zone.risk} Risk
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">Last cleaned: {zone.lastCleaned}</div>
                    <div className="text-sm font-medium">Particulates: {zone.particulates}</div>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center mb-2">
                    <span className="font-medium text-blue-700">Recommended Flooring Solution</span>
                  </div>
                  <div className="text-sm">
                    <p className="font-medium text-blue-800">{zone.recommendedFlooring.product}</p>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {zone.recommendedFlooring.features.map((feature, i) => (
                        <span key={i} className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs">
                          {feature}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WellnessMetrics;